'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/utils/cn';
import { getTemplatesWithConfig } from './assets/templates/maze-template-registry';

interface TemplateGalleryProps {
  onTemplateSelect: (templateId: string) => void;
}

export function TemplateGallery({ onTemplateSelect }: TemplateGalleryProps) {
  const t = useTranslations();
  // Flat, single grid — no category split (owner feedback).
  const templates = getTemplatesWithConfig();

  return (
    <div data-testid="maze-template-gallery" className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
        {templates.map((template) => {
          const slug = template.id.replace('tpl-', '');
          const nameKey = `games.maze.templates.${slug}`;
          const label = t.has(nameKey) ? t(nameKey) : template.name;
          return (
            <button
              key={template.id}
              type="button"
              onClick={() => onTemplateSelect(template.id)}
              data-testid={`maze-tpl-${slug}`}
              className={cn(
                'flex flex-col items-center gap-1.5 rounded-lg border-2 border-muted-foreground/20 bg-muted/30 p-3 transition-all',
                'hover:border-primary/50 hover:bg-primary/5 hover:shadow-xs',
                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              )}
            >
              {template.emoji ? (
                <span className="text-5xl leading-none select-none" role="img" aria-label={label}>
                  {template.emoji}
                </span>
              ) : (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={template.thumbnail}
                  alt={label}
                  className="h-16 w-16 object-contain"
                  loading="eager"
                />
              )}
              <span className="text-xs text-muted-foreground font-medium">
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
